import { Dispatch, SetStateAction, useCallback, useEffect, useRef, useState } from "react";

import logger from "../log";

type Options<T> = {
  jsonEncode?: string extends T ? boolean : false;
};

const debug = logger("utils:storageValue");

export function useLocalStorageState<T>(
  key: string,
  initialValue: T,
  options?: Options<T>,
): [T, Dispatch<SetStateAction<T>>];
export function useLocalStorageState<T>(
  key: string,
  initialValue?: T | undefined,
  options?: Options<T>,
): [T | undefined, Dispatch<SetStateAction<T | undefined>>];

export function useLocalStorageState<T>(
  key: string,
  initialValue?: T | undefined,
  options?: Options<T>,
): [T | undefined, Dispatch<SetStateAction<T | undefined>>] {
  return useStorageState(localStorage, key, initialValue, options as any);
}
export function useSessionStorageState<T>(
  key: string,
  initialValue: T,
  options?: Options<T>,
): [T, Dispatch<SetStateAction<T>>];
export function useSessionStorageState<T>(
  key: string,
  initialValue?: T | undefined,
  options?: Options<T>,
): [T | undefined, Dispatch<SetStateAction<T | undefined>>];

export function useSessionStorageState<T>(
  key: string,
  initialValue?: T | undefined,
  options?: Options<T>,
): [T | undefined, Dispatch<SetStateAction<T | undefined>>] {
  return useStorageState(sessionStorage, key, initialValue, options as any);
}
function useStorageState<T>(
  storage: Storage,
  key: string,
  initialValue: T,
  options?: Options<T>,
): [T, Dispatch<SetStateAction<T>>];
function useStorageState<T>(
  storage: Storage,
  key: string,
  initialValue?: T | undefined,
  options?: Options<T>,
): [T | undefined, Dispatch<SetStateAction<T | undefined>>] {
  const jsonEncode = options?.jsonEncode ?? true;

  const fetch = useCallback(() => {
    try {
      const stored = storage.getItem(key);
      if (!stored) return;
      if (jsonEncode) return JSON.parse(stored) as T;
      return stored as T;
      // eslint-disable-next-line upscope/require-proper-error-handling
    } catch (err) {
      debug.warning("Error fetching storage %O", err);
    }
  }, [storage, key, jsonEncode]);

  const valueRef = useRef<T>();
  const [value, setValue] = useState(valueRef.current ?? fetch() ?? initialValue);
  valueRef.current = value;

  useEffect(() => {
    try {
      if (typeof value === "undefined") {
        storage.removeItem(key);
        return;
      }
      if (jsonEncode) {
        storage.setItem(key, JSON.stringify(value));
        return;
      }
      storage.setItem(key, value as string);
      // eslint-disable-next-line upscope/require-proper-error-handling
    } catch (err) {
      debug.warning("Error fetching storage %O", err);
    }
  }, [key, value, jsonEncode, storage]);

  return [value, setValue];
}
